import { useEffect, useState } from "react"
import type { FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { ConnectivityErrorScreen } from "@/components/connectivity-error/connectivity-error-screen"
import { ErrorBoundaryContentFallback } from "@/components/error-boundary/error-boundary-content-fallback"
import { useAuthentication } from "@/contexts"
import { useTimeout } from "@/nzds/utils"
import { useFlags } from "@/services/feature"
import { isConnectivityError } from "@/utils/api"
import { isUnauthorizedError } from "@/utils/queryClient"
import { easings, useSprings } from "@react-spring/web"

import { AppSplash } from "./app-splash"

interface AppLoaderProps {
  children: ReactNode
}

const minLoadingTime = 1000

interface BootDependency {
  error: unknown
  isFetching: boolean
  refetch: () => Promise<unknown>
}

const isBootDomainError = (error: unknown): boolean =>
  Boolean(error) && !isConnectivityError(error) && !isUnauthorizedError(error)

export const AppLoader: FC<AppLoaderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const flags = useFlags()
  const {
    authenticatedUserError,
    isAuthenticatedUserFetched,
    isAuthenticatedUserFetching,
    isSessionValidFetched,
    isSessionValidFetching,
    refetchAuthenticatedUser,
    refetchSessionValidity,
    sessionError,
  } = useAuthentication()

  const [minLoadingTimeReached, setMinLoadingTimeReached] = useState(false)
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)

  const bootDependencies: BootDependency[] = [
    {
      error: sessionError,
      isFetching: isSessionValidFetching,
      refetch: refetchSessionValidity,
    },
    {
      error: authenticatedUserError,
      isFetching: isAuthenticatedUserFetching,
      refetch: refetchAuthenticatedUser,
    },
    {
      error: flags.flagsError,
      isFetching: flags.flagsIsFetching,
      refetch: flags.flagsRefetch,
    },
  ]
  const bootConnectivityError = bootDependencies.find((dependency) =>
    isConnectivityError(dependency.error)
  )?.error
  const bootDomainError = bootDependencies.find((dependency) =>
    isBootDomainError(dependency.error)
  )?.error
  const isRetrying = bootDependencies.some(
    (dependency) =>
      isConnectivityError(dependency.error) && dependency.isFetching
  )
  const hasFinishedBootPrelude = minLoadingTimeReached && i18n.isInitialized
  const showBootRecovery =
    hasFinishedBootPrelude &&
    !isAnimationComplete &&
    Boolean(bootConnectivityError)
  const showBootDomainError =
    hasFinishedBootPrelude && !isAnimationComplete && Boolean(bootDomainError)
  const isReady =
    hasFinishedBootPrelude &&
    flags.flagsIsFetched &&
    isAuthenticatedUserFetched &&
    isSessionValidFetched &&
    !bootConnectivityError &&
    !bootDomainError

  const springsCount = 2
  const [springs, springsApi] = useSprings(
    springsCount,
    (i) => ({
      from: { opacity: 0, transform: "translateY(60px)" },
      to: { opacity: 1, transform: "translateY(0px)" },
      delay: 200 + i * 40,
      config: (key) => {
        if (key === "opacity") {
          return { duration: 180, easing: easings.easeOutExpo }
        }

        return { duration: 180, easing: easings.easeOutQuart }
      },
    }),
    [springsCount]
  )

  useTimeout(() => {
    setMinLoadingTimeReached(true)
  }, minLoadingTime)

  useEffect(() => {
    if (!isReady || isAnimationComplete) {
      return
    }

    void springsApi.start((i) => ({
      to: { opacity: 0, transform: "translateY(-60px)" },
      delay: i * 40,
      config: (key) => {
        if (key === "opacity") {
          return { duration: 280, easing: easings.easeInExpo }
        }

        return { duration: 280, easing: easings.easeInQuart }
      },
      onRest: () => {
        if (i === springsCount - 1) {
          setIsAnimationComplete(true)
        }
      },
    }))
  }, [isAnimationComplete, isReady, springsApi])

  const handleBootRetry = () => {
    if (isRetrying) {
      return
    }

    void Promise.all(bootDependencies.map((dependency) => dependency.refetch()))
  }

  if (showBootRecovery) {
    return (
      <ConnectivityErrorScreen
        isRetrying={isRetrying}
        onRetry={handleBootRetry}
      />
    )
  }

  if (showBootDomainError) {
    return <ErrorBoundaryContentFallback fullScreen />
  }

  if (!isAnimationComplete) {
    return <AppSplash leftSpring={springs[0]} rightSpring={springs[1]} />
  }

  return <>{children}</>
}
